import * as React from 'react';
import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import type { VariantProps } from 'class-variance-authority';

/**
 * The hierarchy between the variants is the point.
 *
 * `default` (and its alias `primary`) carries the brand purple and a glow - a screen gets
 * **one**. `secondary` is the brand-subtle tint (the same treatment as `soft`): still brand,
 * but a step below the fill. `outline` is glass, the neutral control - Cancel and the like.
 * Repeated row-level actions are `ghost`, which stays quiet until the row is pointed at.
 *
 * Every variant carries the one application radius (`--radius`, 10px) - buttons are not
 * pills. A full radius at 36-44px tall reads playful rather than enterprise, and it made a
 * button impossible to line up with the input, select and card beside it, all of which step
 * to the same corner. The inner top highlight inherits that corner rather than restating it,
 * so the ceiling only ever moves in one place.
 *
 * The legacy variant names (`inline`, `sidebar`) are kept because several hundred call sites
 * use them; they now resolve to the HumAIn treatments rather than the old indigo ones.
 */
const buttonVariants = cva(
	[
		'group/button relative inline-flex shrink-0 select-none items-center justify-center gap-2',
		'whitespace-nowrap rounded-md text-sm font-medium',
		'transition-[background,box-shadow,transform,color,border-color] duration-fast ease-out',
		'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
		'disabled:pointer-events-none disabled:opacity-45 disabled:shadow-none',
		// `aria-disabled` is "not yet": it looks disabled but still takes the click, so the handler
		// can say what is missing. Without this it looked enabled and only a screen reader knew.
		'aria-disabled:cursor-not-allowed aria-disabled:opacity-45 aria-disabled:shadow-none',
	],
	{
		variants: {
			variant: {
				// One flat brand fill. The inner top highlight that used to sit over it was a
				// gradient in all but name, and a gradient on the one purple element per screen
				// is the thing that dates the whole surface.
				default: ['bg-brand text-primary-foreground', 'hover:bg-brand-hover'],
				primary: ['bg-brand text-primary-foreground', 'hover:bg-brand-hover'],
				// Kept as a name, not as a gradient - the call sites asking for one get the fill.
				gradient: ['bg-brand text-primary-foreground', 'hover:bg-brand-hover'],
				// Was the same glass as `outline`, so the two names drew one button and a screen had no
				// way to say "secondary, but still ours". Now the brand-subtle tint; `soft` is kept as
				// its alias for the call sites that already ask for it. The transparent border keeps it
				// the same size as `outline` and gives `pressedControlClass` an edge to colour.
				secondary: 'border border-transparent bg-brand-subtle text-brand-subtle-foreground hover:bg-brand/15',
				outline: [
					'border border-border-control/60 bg-surface/70 text-foreground backdrop-blur-glass',
					'hover:border-brand/40 hover:bg-surface',
				],
				inline: [
					'border border-border-control/60 bg-surface/70 text-brand backdrop-blur-glass',
					'hover:border-brand/40 hover:bg-surface',
				],
				ghost: 'text-muted-foreground hover:bg-brand-subtle hover:text-brand-subtle-foreground',
				// Alias of `secondary`.
				soft: 'border border-transparent bg-brand-subtle text-brand-subtle-foreground hover:bg-brand/15',
				destructive: [
					'border border-destructive-border bg-destructive-subtle text-destructive',
					'hover:border-transparent hover:bg-destructive hover:text-destructive-foreground',
				],
				link: 'h-auto rounded p-0 text-brand underline-offset-4 hover:underline',
				// The nav rail's own active pill. Kept for the handful of places outside the
				// shell that still ask for it.
				sidebar: 'bg-brand-subtle text-brand-subtle-foreground hover:bg-brand-subtle',
				// Neutral ghost: a muted glyph that greys on hover - the 32px icon buttons in
				// viewers, transcripts and pickers (`size="icon-sm"`).
				quiet: 'text-muted-foreground hover:bg-muted hover:text-foreground',
				// No box at all: a bare icon or word in a row or a toolbar, muted until pointed at.
				plain: 'text-muted-foreground hover:text-foreground',
				// `plain` for remove/delete: turns destructive on hover.
				'plain-destructive': 'text-muted-foreground hover:text-destructive',
				// An inline text action in body copy - always underlined, in the text colour.
				'link-neutral': 'text-foreground underline underline-offset-2',
			},
			size: {
				default: 'h-10 px-4 py-2',
				sm: 'h-9 px-3 text-xs',
				md: 'h-9 px-4',
				lg: 'h-11 px-6',
				icon: 'h-10 w-10',
				'icon-sm': 'h-8 w-8',
				// The 36px corner icons in the header.
				'icon-md': 'h-9 w-9',
			},
		},
		compoundVariants: [
			// Boxless variants take no height or padding from `size`, whatever size is passed.
			// `link` was missing here, so the default size's `h-10 px-4` won over its own `p-0` and
			// every link sat 16px in from the text it belongs to.
			{ variant: ['link', 'plain', 'plain-destructive', 'link-neutral'], class: 'h-auto w-auto p-0' },
		],
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
);

/**
 * How a button that stays down looks while it is down.
 *
 * A control that opens something and holds it open - a camera, a panel, a mode - is not the
 * same as one that fires and returns, and until it shows that, the row gives no clue which of
 * its options is currently the open one. Brand edge, brand-subtle fill, and hover held flat so
 * pointing at it does not read as "not yet pressed".
 *
 * Exported so a hand-rolled `<button>` can wear the same thing without copying the classes:
 * `cn(base, isOpen && pressedControlClass)`.
 */
export const pressedControlClass =
	'border-brand bg-brand-subtle text-brand-subtle-foreground hover:border-brand hover:bg-brand-subtle';

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	/**
	 * Held down: a toggle that is currently on, or a control whose panel is open. Sets
	 * `aria-pressed` with it, so the look and what a screen reader hears cannot drift apart.
	 */
	pressed?: boolean;
	/**
	 * The spinner **replaces the leading icon** and the label is untouched, so the button
	 * keeps its width and nothing on the row moves while a request is in flight.
	 */
	pending?: boolean;
	/** The leading icon. Passed rather than nested so `pending` has something to replace. */
	icon?: React.ReactNode;
	/**
	 * A trailing icon, after the label: the arrow on "View all". With `pending` and no leading
	 * `icon`, the spinner takes this icon's place, so the button still keeps its width.
	 */
	iconRight?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, pending = false, pressed, icon, iconRight, children, disabled, ...props }, ref) => {
		// `Slot` takes exactly one child, so a slotted button forwards it untouched. Pending
		// is meaningless there anyway: navigating is not a mutation.
		if (asChild) {
			return (
				<Slot className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props}>
					{children}
				</Slot>
			);
		}

		const spinner = <Loader2 className="relative h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />;
		// The spinner replaces an icon in place: the leading one, or the trailing one when that is all there is.
		const spinTrailing = pending && !icon && Boolean(iconRight);

		return (
			<button
				className={cn(buttonVariants({ variant, size }), pressed && pressedControlClass, className)}
				ref={ref}
				disabled={disabled || pending}
				{...(pending ? { 'aria-busy': true } : {})}
				{...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
				{...props}>
				{pending && !spinTrailing ? spinner : icon}
				{/* The span is a flex row, not a bare wrapper. It exists to sit above the fill's
				    `::before` highlight, and many call sites pass their icon as a *child* rather
				    than via `icon` - wrapped in a plain span those collapse against the label
				    and the gap on the button never applies to them. */}
				{children ? <span className="relative inline-flex items-center gap-2">{children}</span> : null}
				{spinTrailing ? spinner : iconRight}
			</button>
		);
	},
);
Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
